import React, { useState, useEffect } from 'react';
import { ExternalLink, Briefcase, Search, Lock } from 'lucide-react';
import { Text } from '../typography/Text';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from '../common/Link';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../../utils/firebase/config';
import { saveGeneration } from '../../utils/firebase/generations';

interface JobRecommendationsProps {
  jobDescription: string;
  onSave?: (recommendations: string) => void;
}

const functions = getFunctions(app);

interface JobStats {
  jobTitle: string;
  count: number;
  urls: {
    indeed: string;
    zipRecruiter: string;
    simplyHired: string;
    startupJobs: string;
    glassdoor: string;
  };
}

interface JobBoard {
  name: string;
  icon: React.ReactNode;
  color: string;
  logo: string;
  buildUrl: (query: string) => string;
}

const jobBoards: JobBoard[] = [
  {
    name: 'Indeed',
    icon: <Search className="w-4 h-4" />,
    color: 'bg-blue-500/10 text-blue-500',
    logo: 'https://logos-marcas.com/wp-content/uploads/2021/02/Indeed-Emblema.png',
    buildUrl: (query) => `https://www.indeed.com/jobs?q=${query}`
  },
  {
    name: 'ZipRecruiter',
    icon: <Search className="w-4 h-4" />,
    color: 'bg-green-500/10 text-green-500',
    logo: 'https://www.ziprecruiter.com/svc/fotomat/public-nosensitive-ziprecruiter-logos/company/314d8bb3.png',
    buildUrl: (query) => `https://www.ziprecruiter.com/jobs/search?q=${query}`
  },
  {
    name: 'SimplyHired',
    icon: <Search className="w-4 h-4" />,
    color: 'bg-purple-500/10 text-purple-500',
    logo: 'https://d3fw5vlhllyvee.cloudfront.net/one-trust/dist/602d6b6d5efd23646317b6f85c7b12a0/simplyhired/consent/f4c3a99b-82a1-4131-aef7-d89adc2f0361/75534f7d-e437-4fd3-9c04-82d6f98d7d5c/logos/188eb49a-a731-41e5-b6d0-4f5cd571197c/4c23e9c2-3051-437b-8b4b-707e6b1bde4f/c98d9525-2328-4235-96e1-66d3664933b1/simplyhired_square_logo.png',
    buildUrl: (query) => `https://www.simplyhired.com/search?q=${query}`
  },
  {
    name: 'Startup Jobs',
    icon: <Search className="w-4 h-4" />,
    color: 'bg-orange-500/10 text-orange-500',
    logo: 'https://startup.jobs/assets/niches/primary/logo-05fd86c2e87557bcf28aae1f3dc45f0852762c2c11477fcbcb5660b3d673d8cb.svg',
    buildUrl: (query) => `https://startup.jobs/?q=${query}`
  },
  {
    name: 'Glassdoor',
    icon: <Search className="w-4 h-4" />,
    color: 'bg-teal-500/10 text-teal-500',
    logo: 'https://media.glassdoor.com/brand-logo/green-stacked-logo/glassdoor-logo.png',
    buildUrl: (query) => `https://www.glassdoor.com/Job/jobs.htm?sc.keyword=${query}`
  }
];

export function JobRecommendations({ jobDescription }: JobRecommendationsProps) {
  const { subscription } = useAuth();
  const [jobStats, setJobStats] = useState<JobStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';

  useEffect(() => {
    if (!jobDescription) return;

    const generateRecommendations = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const generateJobRecommendationsFunction = httpsCallable(functions, 'generateJobRecommendations');
        const result = await generateJobRecommendationsFunction({ jobDescription });
        const stats = result.data as JobStats;
        
        setJobStats(stats);

        // Save recommendations to history
        const recommendationsData = {
          ...stats,
          timestamp: new Date().toISOString()
        };

        await saveGeneration(
          JSON.stringify(recommendationsData),
          jobDescription,
          { length: 'medium', styles: [], purposes: [] },
          'recommendation'
        );

      } catch (error: any) {
        console.error('Error generating recommendations:', error);
        setError(error.message || 'Failed to generate recommendations');
      } finally {
        setIsLoading(false);
      }
    };

    generateRecommendations();
  }, [jobDescription]);

  if (isLoading || !jobStats) return null;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-primary/10 p-2 rounded-lg">
            <Briefcase className="w-5 h-5 text-primary" />
          </div>
          <div>
            <Text className="text-slate font-medium">
              {jobStats.jobTitle && <span className="font-semibold">{jobStats.jobTitle}:</span>} {jobStats.count.toLocaleString()} relevant positions currently open
            </Text>
          </div>
        </div>
      </div>

      <div className="relative">
        {!hasActiveSubscription && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px] z-10 flex items-center justify-center">
            <div className="text-center px-6">
              <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-primary" />
              </div>
              <Text className="text-slate font-medium mb-2">
                Unlock Job Recommendations
              </Text>
              <Text className="text-slate-light mb-4">
                Upgrade to Pro to access job recommendations and apply with confidence
              </Text>
              <Link
                href="/pricing"
                className="inline-flex items-center px-6 py-2 bg-primary text-white rounded-full hover:bg-primary-hover transition-colors"
              >
                Upgrade Now
              </Link>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-4">
          {jobBoards.map((board) => (
            <a
              key={board.name}
              href={hasActiveSubscription ? board.buildUrl(jobStats.jobTitle.toLowerCase()) : '/pricing'}
              target={hasActiveSubscription ? "_blank" : "_self"}
              rel={hasActiveSubscription ? "noopener noreferrer" : ""}
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-300 ${
                board.color
              }`}
            >
              <img 
                src={board.logo} 
                alt={`${board.name} logo`}
                className="h-6 object-contain"
              />
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">{board.name}</span>
                <ExternalLink className="w-3 h-3" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}